/**
 * 🆕 28/07/2026 — Dialog "Avvisa cliente della scadenza": scelta canale (Email / WhatsApp / Entrambi).
 * Precompila email e cellulare dal profilo cliente e disabilita i canali senza dati.
 * Uso backend: POST /api/policies/{id}/send-expiry-notice?channels=email|whatsapp|both
 * Per il canale WhatsApp apre wa.me in nuova scheda dopo la risposta.
 */
import { useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, formatApiError, normalizeMobileIntl } from "@/lib/api";

const CHANNELS = [
  { v: "email", label: "Solo Email", icon: "✉️" },
  { v: "whatsapp", label: "Solo WhatsApp", icon: "💬" },
  { v: "both", label: "Entrambi", icon: "📨", default: true },
];

export function ExpiryNoticeDialog({ open, policy, client, onClose, onSent }) {
  const clientEmail = client?.portal_email || client?.email || "";
  const clientMobile = client?.mobile || client?.phone || "";
  const hasEmail = Boolean(clientEmail);
  const hasMobile = Boolean(normalizeMobileIntl(clientMobile));
  const defaultChannel = useMemo(() => {
    if (hasEmail && hasMobile) return "both";
    if (hasEmail) return "email";
    if (hasMobile) return "whatsapp";
    return "both";
  }, [hasEmail, hasMobile]);
  const [channel, setChannel] = useState(defaultChannel);
  const [sending, setSending] = useState(false);
  // 🆕 Feb 2026 — Lock sincrono anti-doppio-click (setState è async, può non applicarsi prima
  // di un secondo click rapido). Senza questo lock, due click rapidi fanno partire 2 POST.
  const submitLockRef = useRef(false);

  // Sync default quando cambia policy/client
  useMemo(() => setChannel(defaultChannel), [defaultChannel]);

  const isChannelDisabled = (v) => (v === "email" && !hasEmail) || (v === "whatsapp" && !hasMobile) || (v === "both" && !hasEmail && !hasMobile);
  const canSubmit = !sending && !isChannelDisabled(channel);

  const handleSubmit = async () => {
    if (!policy?.id) return;
    // 🆕 Feb 2026 — Guard sincrono: se già in corso un invio, ignora la seconda invocazione.
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    setSending(true);
    try {
      const { data } = await api.post(`/policies/${policy.id}/send-expiry-notice?channels=${channel}`);
      if (data.wa_url) window.open(data.wa_url, "_blank", "noopener,noreferrer");
      const parts = [];
      if (data.email_sent) parts.push(`email inviata a ${data.target_email}`);
      if (data.wa_url) parts.push("WhatsApp aperto");
      toast.success(parts.length ? `Avviso inviato — ${parts.join(" · ")}` : "Nessun canale attivo");
      onSent?.(data);
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Invio avviso fallito");
    } finally {
      setSending(false);
      submitLockRef.current = false;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="expiry-notice-dialog">
        <DialogHeader>
          <DialogTitle>📢 Avvisa cliente della scadenza</DialogTitle>
          <DialogDescription>
            Polizza <b>{policy?.policy_number || "—"}</b> · {policy?.company || "—"}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded border p-2 bg-slate-50 text-xs">
            <div>📧 <b>Email</b>: {clientEmail || <span className="text-rose-600">non disponibile</span>}</div>
            <div>📱 <b>Cellulare</b>: {clientMobile ? `${clientMobile}${hasMobile ? "" : " (formato non valido)"}` : <span className="text-rose-600">non disponibile</span>}</div>
          </div>
          <div className="space-y-1.5">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Canale di invio</div>
            {CHANNELS.map((c) => {
              const disabled = isChannelDisabled(c.v);
              return (
                <label
                  key={c.v}
                  data-testid={`channel-option-${c.v}`}
                  className={`flex items-center gap-2 p-2 rounded border cursor-pointer text-sm ${channel === c.v ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:bg-slate-50"} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <input
                    type="radio"
                    name="expiry-channel"
                    value={c.v}
                    checked={channel === c.v}
                    disabled={disabled}
                    onChange={() => setChannel(c.v)}
                    className="cursor-pointer"
                  />
                  <span>{c.icon}</span>
                  <span className="font-medium">{c.label}</span>
                  {disabled && <span className="ml-auto text-[11px] text-rose-600">non disponibile</span>}
                </label>
              );
            })}
          </div>
          {channel === "whatsapp" && (
            <p className="text-[11px] text-slate-500">Al conferma verrà aperta una nuova scheda WhatsApp con il messaggio precompilato. Basta un tap per inviare.</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onClose?.()} disabled={sending} data-testid="expiry-notice-cancel">Annulla</Button>
          <Button onClick={handleSubmit} disabled={!canSubmit} data-testid="expiry-notice-submit" className="bg-blue-600 hover:bg-blue-700 text-white">
            {sending ? "Invio…" : "Invia avviso"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ExpiryNoticeDialog;
